import React from 'react'
import { useTranslation } from 'react-i18next'
import * as R from 'remeda'

import { useHighlightCells } from '~/hooks/useHighlightCells'
import { type Question } from '~/hooks/useSurveyQuery'
import { useTamRatio } from '~/hooks/useTamRatio'
import {
  getDiffKey,
  type GroupAndAll,
  mapItemsToObj,
  type RowInfo,
} from '~/utils'

import {
  useAtomValueContext,
  useSetAtomContext,
} from '../providers/AtomsProvider'
import {
  buildMatrixTableInfo,
  getAnswerOptionRowId,
  type MatrixColumnKey,
} from '../utils'

import { useGetSortIcon } from './useGetSortIcon'
import { useMapQuestionsDiff } from './useMapQuestionsDiff'
import { useMatrixAnswersData } from './useMatrixAnswersData'
import { useSortFunc } from './useSortFunc'
import { useSubQuestions } from './useSubQuestions'
import { useTotalData } from './useTotalData'

type MatrixTableInfoStyles = {
  answerText: string
  answerTextHeader: string
}

export const useMatrixTableInfo = (
  question: Question,
  group: GroupAndAll,
  styles: MatrixTableInfoStyles,
): RowInfo<MatrixColumnKey>[] => {
  const { t } = useTranslation()
  const groupNameMap = useAtomValueContext('groupNameMap')
  const setNameEditingGroup = useSetAtomContext('nameEditingGroup')

  const subQuestions = useSubQuestions(question)
  const mapSubQuestions = React.useCallback(
    <T>(fn: (subQuestion: Question) => T) =>
      mapItemsToObj(subQuestions, fn, 'question_id'),
    [subQuestions],
  )

  const getSortIcon = useGetSortIcon('matrixSortValue')
  const mapQuestionsDiff = useMapQuestionsDiff(question)
  const totalData = useTotalData()
  const tamRatio = useTamRatio()
  const dataDisplayType = useAtomValueContext('dataDisplayType')
  const { addHighlightProps } = useHighlightCells(`matrix-${group}`)

  const answersData = useMatrixAnswersData(question, group)
  const hiddenRowIds = useAtomValueContext('hiddenRowIds')
  const visibleAnswersData = React.useMemo(
    () =>
      R.differenceWith(
        answersData,
        hiddenRowIds,
        ({ answerOption }, hiddenRowId) =>
          getAnswerOptionRowId(question, answerOption) === hiddenRowId,
      ),
    [answersData, hiddenRowIds, question],
  )

  const sortFunc = useSortFunc('matrixSortValue')

  const diffMinMaxMap = React.useMemo(
    () =>
      mapQuestionsDiff((questionsDiff) => {
        const ratioArray = answersData.map(
          (answerData) => answerData.ratios[getDiffKey(questionsDiff)],
        )

        return { max: Math.max(...ratioArray), min: Math.min(...ratioArray) }
      }),
    [answersData, mapQuestionsDiff],
  )

  return React.useMemo(
    () =>
      buildMatrixTableInfo({
        addHighlightProps,
        dataDisplayType,
        diffMinMaxMap,
        getSortIcon,
        group,
        groupNameMap,
        mapQuestionsDiff,
        mapSubQuestions,
        setNameEditingGroup,
        sortFunc,
        styles: {
          answerText: styles.answerText,
          answerTextHeader: styles.answerTextHeader,
        },
        t,
        tamRatio,
        totalData: { counts: totalData.counts, ratios: totalData.ratios },
        visibleAnswersData,
      }),
    [
      addHighlightProps,
      dataDisplayType,
      diffMinMaxMap,
      getSortIcon,
      group,
      groupNameMap,
      mapQuestionsDiff,
      mapSubQuestions,
      setNameEditingGroup,
      sortFunc,
      styles.answerText,
      styles.answerTextHeader,
      t,
      tamRatio,
      totalData.counts,
      totalData.ratios,
      visibleAnswersData,
    ],
  )
}
